import React, { useState, useContext, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useProgram } from '../ProgramContext';
import AuthContext from '../AuthContext';
import LanguageContext from '../LanguageContext';
import api from '../axiosInstance';
import useSignOut from '../hooks/useSignOut';

function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const { isAuthenticated, user } = useContext(AuthContext);
  const { language, selectedLanguage, setLanguage } = useContext(LanguageContext);
  const [unreadCount, setUnreadCount] = useState(0);
  const [documentsSummary, setDocumentsSummary] = useState({ totalCount: 0, activeCount: 0 });
  const signOut = useSignOut();
  const { program } = useProgram();
  const shellStripStyle = {
    '--app-shell-strip-image': `url(${process.env.PUBLIC_URL || ''}/Headerfooter_strip_Create_a_thin_decorative_horizontal_band_for_a_website_head_20260316173810_02.png)`
  };

  const text = {
    en: {
      iset: 'NWAC ISET Online',
      jordan: "Jordan's Application Intake - v 04.09.2025",
      helpSupport: 'Help/Support',
      accessibility: 'Accessibility',
      english: 'English',
      french: 'Français',
      signOut: 'Sign Out',
      messages: 'Messages',
      myDocuments: 'My documents',
      changePassword: 'Change password',
      signIn: 'Sign In',
      register: 'Register'
    },
    fr: {
      iset: 'NWAC ISET Online',
      jordan: 'Demande d\'admission - Principe de Jordan - v 04.09.2025',
      helpSupport: 'Aide/Soutien',
      accessibility: 'Accessibilité',
      english: 'English',
      french: 'Français',
      signOut: 'Déconnexion',
      messages: 'Messages',
      myDocuments: 'Mes documents',
      changePassword: 'Changer le mot de passe',
      signIn: 'Connexion',
      register: 'Inscription'
    }
  };
  const uiLanguage = text[selectedLanguage] ? selectedLanguage : language;
  const alternateLanguage = selectedLanguage === 'fr' ? 'en' : 'fr';
  const alternateLanguageLabel = alternateLanguage === 'fr' ? text.en.french : text.fr.english;

  const toggleMenu = (event) => {
    event.stopPropagation();
    setMenuOpen(!menuOpen);
  };

  const handleSignOut = async (event) => {
    event.preventDefault();
    setMenuOpen(false);
    await signOut({ redirectTo: '/sign-out', hostedLogout: false });
  };

  const handleClickOutside = (event) => {
    if (!event.target.closest('.govuk-header__navigation') && !event.target.closest('.govuk-header__menu-icon')) {
      setMenuOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  const handleLinkClick = () => {
    setMenuOpen(false);
  };

  const currentUserId = user?.id;

  useEffect(() => {
    if (!isAuthenticated || !currentUserId) {
      setUnreadCount(0);
      setDocumentsSummary({ totalCount: 0, activeCount: 0 });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [messagesResult, documentsResult] = await Promise.allSettled([
          api.get('/api/messages/summary'),
          api.get('/api/signing-requests/summary'),
        ]);
        if (cancelled) return;
        if (messagesResult.status === 'fulfilled') {
          setUnreadCount(Number(messagesResult.value?.data?.unreadCount) || 0);
        }
        if (documentsResult.status === 'fulfilled') {
          setDocumentsSummary({
            totalCount: Number(documentsResult.value?.data?.totalCount) || 0,
            activeCount: Number(documentsResult.value?.data?.activeCount) || 0,
          });
        }
      } catch (e) {
        // Silently ignore 401/403 (likely race before cookies) – optional console.debug
        // if (process.env.NODE_ENV !== 'production') console.debug('[Header] messages fetch skipped:', e?.response?.status);
      }
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated, currentUserId]);

  return (
    <>
      <header className="govuk-header app-header-shell" role="banner" style={{ padding: '15px' }}>
        <div className="govuk-width-container govuk-header__container">
          <Link to={isAuthenticated ? '/dashboard' : '/'} className="govuk-header__logotype govuk-header__link" onClick={handleLinkClick}>
            {program === 'Jordan'
              ? text[uiLanguage].jordan
              : text[uiLanguage].iset}
          </Link>

          <button
            className="govuk-header__menu-icon"
            aria-label="Toggle navigation"
            style={{background: 'none', border: 'none', color: 'white', fontSize: '24px' }}
            onClick={toggleMenu}
          >
            ☰
          </button>

          <nav className={`govuk-header__navigation ${menuOpen ? 'open' : ''}`} aria-label="Top Level Navigation">
            <ul className="govuk-header__navigation-list">
              <li className="govuk-header__navigation-item">
                <Link to="/help" className="govuk-header__link" onClick={handleLinkClick}>
                  {text[uiLanguage].helpSupport}
                </Link>
              </li>

              {isAuthenticated ? (
                <>
                  <li className="govuk-header__navigation-item">
                    <Link to="/messages" className="govuk-header__link" onClick={handleLinkClick}>
                      {unreadCount > 0 ? `${text[uiLanguage].messages}(${unreadCount})` : text[uiLanguage].messages}
                    </Link>
                  </li>
                  {documentsSummary.totalCount > 0 && (
                    <li className="govuk-header__navigation-item">
                      <Link to="/documents" className="govuk-header__link" onClick={handleLinkClick}>
                        {documentsSummary.activeCount > 0
                          ? `${text[uiLanguage].myDocuments}(${documentsSummary.activeCount})`
                          : text[uiLanguage].myDocuments}
                      </Link>
                    </li>
                  )}
                  <li className="govuk-header__navigation-item">
                    <Link to="/change-password" className="govuk-header__link" onClick={handleLinkClick}>
                      {text[uiLanguage].changePassword}
                    </Link>
                  </li>
                  <li className="govuk-header__navigation-item">
                    <a href="/sign-out" onClick={handleSignOut} className="govuk-header__link">{text[uiLanguage].signOut}</a>
                  </li>
                </>
              ) : (
                <>
                  <li className="govuk-header__navigation-item">
                    <Link
                      to={isAuthenticated ? '/dashboard' : '/login'}
                      className="govuk-header__link"
                      onClick={handleLinkClick}
                    >
                      {text[uiLanguage].signIn}
                    </Link>
                  </li>
                  <li className="govuk-header__navigation-item">
                    <Link to="/register" className="govuk-header__link" onClick={handleLinkClick}>
                      {text[uiLanguage].register}
                    </Link>
                  </li>
                </>
              )}
              <li className="govuk-header__navigation-item">
                <a
                  href={`/${alternateLanguage}`}
                  className="govuk-header__link"
                  lang={alternateLanguage}
                  hrefLang={alternateLanguage}
                  onClick={(event) => {
                    event.preventDefault();
                    setLanguage(alternateLanguage);
                    setMenuOpen(false);
                  }}
                >
                  {alternateLanguageLabel}
                </a>
              </li>
            </ul>
          </nav>
        </div>
      </header>
      <div
        className="app-shell-strip app-header-strip"
        aria-hidden="true"
        style={shellStripStyle}
      />
    </>
  );
}

export default Header;

